import React, { useState, useEffect } from 'react';
import { Popover } from '@material-ui/core';

import ConfirmationMessage from '../Controls/ConfirmationMessage';

import styles from './CustomFilters.module.css';

const ignoreElementsClasses = [
    styles.loadFiltersContainer,
    styles.loadFiltersContainerBtn,
    styles.customFilterDropdownItem,
    styles.customFilterItemName,
    styles.deleteCustomFilter,
    styles.loadFiltersContainerIcon,
    styles.selectedCustomFilter,
    styles.customFilterSearchInput,
    styles.customFilterSearchContainer,
    styles.customFiltersDropdownItems,
    styles.customFilterItemDate,
    styles.loadFiltersBtnText
];
const CustomFilters = ({
    currentCustomFilters,
    saveCustomFilters,
    deleteCustomFilter,
    loadCustomFilter,
    selectedCustomFilter,
    customReports,
    rangeFilter
}) => {
    const [saveFilterEl, setSaveFilterEl] = useState(null);
    const [saveFilterErrorMsg, setSaveFilterErrorMsg] = useState('');
    const [newCustomFilterName, setNewCustomFilterName] = useState('');
    const [showCustomFiltersDropdown, setShowCustomFiltersDropdown] = useState(false);
    const [showDeleteFilterModal, setShowDeleteFilterModal] = useState(false);
    const [deletedFilterName, setDeletedFilterName] = useState('');
    const [searchText, setSearchText] = useState('');

    const currentCustomFiltersNames = Object.keys(currentCustomFilters).map((filterName) => filterName.toLowerCase());

    let allowUpdateFilter = true;

    if (currentCustomFiltersNames.includes(newCustomFilterName.toLowerCase())) {
        const hasReport = Object.values(customReports).some((customReport) => {
            return customReport.customFilters.some((customFilter) => {
                return customFilter.toLowerCase() === newCustomFilterName.toLowerCase();
            });
        });
        if (hasReport && !rangeFilter) {
            allowUpdateFilter = false;
        }
    }

    useEffect(() => {
        if (saveFilterErrorMsg) {
            const timer = setTimeout(() => setSaveFilterErrorMsg(''), 5000);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [saveFilterErrorMsg]);

    useEffect(() => {
        const clickEventListener = (event) => {
            const targetElement = event.target;
            const ignoreElements = [];
            for (const currentClassName of ignoreElementsClasses) {
                ignoreElements.push(document.getElementsByClassName(currentClassName));
            }

            const ignoreElementsSet = new Set();
            for (const collection of ignoreElements) {
                for (const element of collection) {
                    ignoreElementsSet.add(element);
                }
            }
            if (ignoreElementsSet.has(targetElement)) {
                return;
            }
            setShowCustomFiltersDropdown(false);
        };
        window.addEventListener('click', clickEventListener);
        return () => {
            window.removeEventListener('click', clickEventListener);
        };
    }, []);

    const onSaveFiltersClicked = () => {
        if (saveCustomFilters && allowUpdateFilter) {
            const success = saveCustomFilters(newCustomFilterName);
            if (!success) {
                setSaveFilterErrorMsg('Failed to save custom filter');
            } else {
                setNewCustomFilterName('');
                setSaveFilterEl(null);
            }
        }
    };

    const onDeleteFilterClicked = () => {
        if (deleteCustomFilter) {
            deleteCustomFilter(deletedFilterName);
        }
        setDeletedFilterName('');
        setShowDeleteFilterModal(false);
    };

    const onLoadFilterClicked = (customFilterName) => {
        const successes = loadCustomFilter(customFilterName);
        if (successes) {
            setShowCustomFiltersDropdown(false);
        }
    };

    const customFiltersOptions = Object.keys(currentCustomFilters)
        .filter((customFilterName) => {
            return customFilterName.toLowerCase().includes(searchText.toLowerCase());
        })
        .sort()
        .map((customFilterName) => {
            const reportsWithFilter = Object.values(customReports)
                .filter((customReport) => {
                    return customReport.customFilter === customFilterName;
                })
                .map((customReport) => customReport.reportName);
            return (
                <div
                    className={styles.customFilterDropdownItem}
                    key={customFilterName}
                    onClick={(e) => {
                        e.stopPropagation();
                        onLoadFilterClicked(customFilterName);
                    }}>
                    <div className={styles.customFilterItemName} title={customFilterName}>
                        {customFilterName}
                        <div className={styles.customFilterItemDate}>
                            {currentCustomFilters[customFilterName].creationDate}
                        </div>
                    </div>
                    <i
                        title={
                            reportsWithFilter.length > 0
                                ? `Report${reportsWithFilter.length > 1 ? 's' : ''} [${reportsWithFilter}] ${
                                      reportsWithFilter.length > 1 ? 'are' : 'is'
                                  } using this filter`
                                : ''
                        }
                        onClick={(e) => {
                            if (reportsWithFilter.length === 0) {
                                e.stopPropagation();
                                setDeletedFilterName(customFilterName);
                                setShowDeleteFilterModal(true);
                            }
                        }}
                        className={`${styles.deleteCustomFilter} ${
                            reportsWithFilter.length > 0 ? styles.disabledDeleteCustomFilter : ''
                        } fa fa-trash-o`}></i>
                </div>
            );
        });

    return (
        <div className={styles.customFiltersContainer}>
            <div className={styles.saveFiltersContainer}>
                <div
                    className={`${styles.saveFilterBtn} ${Boolean(saveFilterEl) ? styles.selectedBtn : ''}`}
                    onClick={(e) => {
                        setSaveFilterEl(e.currentTarget);
                    }}>
                    <i className="fa fa-save"></i>
                    Save filters
                </div>
                <Popover
                    id={'saveFilterPopover'}
                    open={Boolean(saveFilterEl)}
                    anchorEl={saveFilterEl}
                    onClose={() => {
                        setSaveFilterEl(null);
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}>
                    <div className={styles.saveFilterPopover}>
                        <div className={styles.saveFilterPopoverMain}>
                            <input
                                type="text"
                                className="form-control"
                                name="SearchQuery"
                                placeholder="Custom filter name..."
                                maxLength="30"
                                onChange={(e) => setNewCustomFilterName(e.target.value)}
                                value={newCustomFilterName}
                            />
                            <div
                                className={`${styles.saveFilterApproveBtn} ${
                                    !allowUpdateFilter ? styles.disabledSaveFilterApproveBtn : ''
                                }`}
                                onClick={onSaveFiltersClicked}>
                                {!currentCustomFiltersNames.includes(newCustomFilterName.toLowerCase())
                                    ? 'Save'
                                    : 'Update'}
                            </div>
                        </div>
                        {saveFilterErrorMsg && (
                            <div className={styles.saveFilterPopoverErrorMsg}>{saveFilterErrorMsg}</div>
                        )}
                    </div>
                </Popover>
            </div>
            <div className={styles.splitter}></div>
            <div className={styles.loadFiltersContainer}>
                <div
                    className={`${styles.loadFiltersContainerBtn} ${
                        showCustomFiltersDropdown ? styles.selectedBtn : ''
                    }`}
                    onClick={() => {
                        setShowCustomFiltersDropdown(!showCustomFiltersDropdown);
                    }}>
                    <div className={styles.loadFiltersBtnText}>
                        Open saved filters
                        {selectedCustomFilter ? `: ${selectedCustomFilter}` : ''}
                    </div>
                    {showCustomFiltersDropdown && (
                        <i className={`fa fa-angle-up ${styles.loadFiltersContainerIcon}`}></i>
                    )}
                    {!showCustomFiltersDropdown && (
                        <i className={`fa fa-angle-down ${styles.loadFiltersContainerIcon}`}></i>
                    )}
                </div>
                {showCustomFiltersDropdown && (
                    <div className={styles.customFiltersDropdown}>
                        <div className={styles.customFilterSearchContainer}>
                            <input
                                className={styles.customFilterSearchInput}
                                autoFocus
                                defaultValue={searchText}
                                onChange={(event) => {
                                    setSearchText(event.target.value);
                                }}
                                placeholder={'Search custom filter...'}
                            />
                        </div>
                        <div className={styles.customFiltersDropdownItems}>
                            {customFiltersOptions.length > 0 ? customFiltersOptions : 'There are no filters'}
                        </div>
                    </div>
                )}
            </div>
            {showDeleteFilterModal && (
                <ConfirmationMessage
                    title={'Delete Custom Filter'}
                    body={`Are you sure you want to delete "${deletedFilterName}" ?`}
                    approveText={'Delete'}
                    cancelText={'Cancel'}
                    approveStyle={'danger'}
                    OnCancel={() => setShowDeleteFilterModal(false)}
                    OnApprove={onDeleteFilterClicked}
                />
            )}
        </div>
    );
};

export default CustomFilters;
